/**
 * FoundationPanel — honest mechanization status for RD-25.
 *
 * RD-25 is SETTLED. ZKP-1..ZKP-6 are PROVED with zero postulates across eight
 * Agda modules in agda/Research/ZeroKnowledgeCompliance/. This panel reads the
 * local `data/theorems.json` and states what is proved and the open sub-goals
 * (ZKP-2 external DAG, ZKP-5 minimality bridge) as exactly what they are —
 * pointers, not transcribed proofs.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import type { MonographDataTableColumn } from "@csk/demo-ui";
import { AgdaSourceLink, PanelSurface, panelTokens } from "@csk/demo-ui";
import theoremsData from "../data/theorems.json" with { type: "json" };

type ConjectureRow = (typeof theoremsData.conjectures)[number];

function statusColor(status: string): string {
  return status === "PROVED" ? panelTokens.immuneGreen : panelTokens.accentYellow;
}

const CONJECTURE_COLUMNS: readonly MonographDataTableColumn<ConjectureRow>[] = [
  {
    accessorKey: "id",
    header: "Code",
    cell: ({ row }) => (
      <span className="font-mono text-[12.5px] font-bold text-[var(--zkp-bright)]">
        {row.original.id}
      </span>
    ),
  },
  {
    accessorKey: "label",
    header: "Conjecture",
    cell: ({ row }) => (
      <span className="text-[12.5px] text-[var(--zkp-text)]">
        {row.original.label}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) =>
      PanelSurface.badge(row.original.status, statusColor(row.original.status)),
  },
  {
    accessorKey: "agdaFile",
    header: "Agda module",
    cell: ({ row }) => <AgdaSourceLink module={row.original.agdaFile} />,
  },
];

export function FoundationPanel(): React.JSX.Element {
  const conditional = theoremsData.conjectures.filter(
    (conjecture) => conjecture.conditional !== null,
  );
  return (
    <PanelSurface.PanelShell
      title="Foundation & Mechanization"
      subtitle="RD-25 is SETTLED. ZKP-1 through ZKP-6 are proved with zero postulates across eight Agda modules. Below is the honest status: what is proved, and the open sub-goals stated as exactly what they are."
      accentColor={panelTokens.accentTeal}
    >
      <PanelSurface.SectionHeading>Headline status</PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="RD-25 SETTLED — six conjectures proved, zero postulates"
        tone="safe"
        color={panelTokens.immune}
      >
        <PanelSurface.Prose className="m-0">
          The mechanization is complete across eight modules in{" "}
          <code className="font-mono">
            agda/Research/ZeroKnowledgeCompliance/
          </code>
          . ZKP-1 is definitional (
          <code className="font-mono">IsCompliantByZKP = MorphismExistsContent</code>
          ); ZKP-3 — the central theorem — is proved by{" "}
          <code className="font-mono">zkp3-all-canonical-systems</code>, one
          universal proof covering all four generator classes; ZKP-4 via
          discharge-compose; ZKP-6 by the generator-decomposition record. Every
          module is <code className="font-mono">--safe --without-K</code> with{" "}
          <strong className="text-[var(--zkp-bright)]">no postulates</strong>.
          The anchors below are the normative proof path; this panel is pointers,
          not transcribed proofs.
        </PanelSurface.Prose>
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>
        The six conjectures
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.DataTable
          ariaLabel="RD-25 conjecture proof status and Agda location"
          data={theoremsData.conjectures}
          columns={CONJECTURE_COLUMNS}
          getRowId={(row) => row.id}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        What each conjecture states
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ContentList
          ariaLabel="RD-25 conjecture statements"
          variant="plain"
          items={theoremsData.conjectures.map((conjecture) => ({
            id: conjecture.id,
            title: `${conjecture.id} — ${conjecture.label}`,
            body: conjecture.statement,
            color: statusColor(conjecture.status),
          }))}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Open sub-goals, stated honestly
      </PanelSurface.SectionHeading>
      <PanelSurface.Callout
        title="Proved theorems with named open bridges — not gaps in the main results"
        tone="warn"
        color={panelTokens.accentGold}
      >
        <PanelSurface.Prose className="m-0">
          Two conjectures carry open sub-goals that are{" "}
          <em>categorical-bridge formalizations</em>, not gaps in the proved
          theorems themselves:
        </PanelSurface.Prose>
        <div className="mt-3 space-y-2.5">
          {conditional.map((conjecture) => (
            <div
              key={conjecture.id}
              className="rounded-lg border border-dashed p-2.5"
              style={{
                borderColor: `${panelTokens.accentGold}55`,
                background: `${panelTokens.accentGold}10`,
              }}
            >
              <div className="font-mono text-[12px] font-bold text-[var(--zkp-bright)]">
                {conjecture.id} · {conjecture.label}
              </div>
              <div className="mt-1 text-[12px] leading-5 text-[var(--zkp-text)]">
                {conjecture.conditional}
              </div>
            </div>
          ))}
        </div>
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>The naming triad</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.FactList
          ariaLabel="RD-25 naming triad"
          items={[
            {
              id: "zkp-security",
              label: "Security",
              value: theoremsData.namingTriad.security,
            },
            {
              id: "zkp-governance",
              label: "Governance",
              value: theoremsData.namingTriad.governance,
            },
            {
              id: "zkp-mathematical",
              label: "Mathematical",
              value: theoremsData.namingTriad.mathematical,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-25 §6, §7 — conjectures and status",
            path: "research/settled/RD-25-ZERO-KNOWLEDGE-COMPLIANCE-PROOFS.md",
          },
          {
            label: "ComplianceZKP.agda (ZKP-1, ZKP-4)",
            path: "agda/Research/ZeroKnowledgeCompliance/ComplianceZKP.agda",
          },
          {
            label: "CanonicalAuthorityDAG.agda (ZKP-2)",
            path: "agda/Research/ZeroKnowledgeCompliance/CanonicalAuthorityDAG.agda",
          },
          {
            label: "GeneratorSoundness.agda (ZKP-3 all generators)",
            path: "agda/Research/ZeroKnowledgeCompliance/GeneratorSoundness.agda",
          },
          {
            label: "CanonicalSoundness.agda (ZKP-3 PERMIT)",
            path: "agda/Research/ZeroKnowledgeCompliance/CanonicalSoundness.agda",
          },
          {
            label: "CircuitCanonicity.agda (ZKP-5)",
            path: "agda/Research/ZeroKnowledgeCompliance/CircuitCanonicity.agda",
          },
          {
            label: "GeneratorCircuitDecomposition.agda (ZKP-6)",
            path: "agda/Research/ZeroKnowledgeCompliance/GeneratorCircuitDecomposition.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
